/**
 * Everything needed to offer "install this on your phone", which no platform
 * offers loudly enough on its own: Android shows its own banner only after its
 * engagement heuristics are satisfied, and iOS never shows anything at all —
 * there the user has to know about Share → Add to Home Screen.
 *
 * The browser fires `beforeinstallprompt` once, early, and only when the app
 * really is installable. Capturing it at import time rather than in a mounted
 * component is what keeps it from being missed.
 */

const DISMISSED_KEY = 'hit.install-hint-dismissed'

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: InstallPromptEvent | null = null
const subscribers = new Set<() => void>()

const notify = (): void => subscribers.forEach((subscriber) => subscriber())

export const captureInstallPrompt = (target: Window = window): void => {
  target.addEventListener('beforeinstallprompt', (event) => {
    // Without this the browser shows its own bar on its own schedule.
    event.preventDefault()
    deferred = event as InstallPromptEvent
    notify()
  })

  target.addEventListener('appinstalled', () => {
    deferred = null
    notify()
  })
}

export const hasInstallPrompt = (): boolean => deferred !== null

export const onInstallPromptChange = (subscriber: () => void): (() => void) => {
  subscribers.add(subscriber)

  return () => {
    subscribers.delete(subscriber)
  }
}

/**
 * Shows the browser's own install dialog. The captured event is single use, so
 * it is dropped either way.
 */
export const showInstallPrompt = async (): Promise<boolean> => {
  if (deferred === null) {
    return false
  }

  const event = deferred
  deferred = null
  notify()

  await event.prompt()
  const { outcome } = await event.userChoice

  return outcome === 'accepted'
}

/**
 * Whether the page is already running as an installed app. Reads `.matches`
 * once and never subscribes: a `change` listener on a media query hangs every
 * Pest browser test.
 */
export const isInstalled = (target: Window = window): boolean => {
  const standalone = (target.navigator as Navigator & { standalone?: boolean })
    .standalone

  return (
    target.matchMedia?.('(display-mode: standalone)').matches === true ||
    standalone === true
  )
}

/**
 * iPadOS reports itself as a Mac, which only a touch screen tells apart.
 */
export const isIos = (target: Window = window): boolean => {
  const ua = target.navigator.userAgent

  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && target.navigator.maxTouchPoints > 1)
  )
}

export const isDismissed = (
  storage: Storage | undefined = undefined,
): boolean => {
  try {
    return (storage ?? window.localStorage).getItem(DISMISSED_KEY) === '1'
  } catch {
    // Private mode, or storage turned off. Offering the hint again is harmless.
    return false
  }
}

export const dismiss = (storage: Storage | undefined = undefined): void => {
  try {
    ;(storage ?? window.localStorage).setItem(DISMISSED_KEY, '1')
  } catch {
    // Nothing to do: the hint simply comes back next time.
  }
}

export type InstallHint = 'none' | 'prompt' | 'ios'

/**
 * What, if anything, to offer the user.
 *
 * `prompt` is only ever returned when the browser has told us the app is
 * installable, so the button can never lead to a dead end. `ios` is the
 * instruction, because Safari exposes no event and no API for this.
 */
export const installHint = ({
  installed,
  dismissed,
  promptAvailable,
  ios,
}: {
  installed: boolean
  dismissed: boolean
  promptAvailable: boolean
  ios: boolean
}): InstallHint => {
  if (installed || dismissed) {
    return 'none'
  }

  if (promptAvailable) {
    return 'prompt'
  }

  return ios ? 'ios' : 'none'
}
